import React, { useEffect, useState } from "react";
import {
  listPlatformUserRouteDeclarationRevisions,
  downloadPlatformDocumentRevision,
  sendRouteDeclarationForSignature,
} from "../services/api";

function mapStatusLabel(s) {
  if (!s) return "—";
  const m = {
    generated: "Generat",
    sent_for_signature: "Trimis la semnare",
    signed: "Semnat",
    failed: "Eșuat",
    cancelled: "Anulat",
  };
  return m[s] || s;
}

export default function RouteDeclarationRevisions({ userId }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [sending, setSending] = useState(null);

  async function fetchList() {
    setLoading(true);
    setErr("");
    try {
      const data = await listPlatformUserRouteDeclarationRevisions(userId);
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare declarații de traseu");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");
      try {
        const data = await listPlatformUserRouteDeclarationRevisions(userId);
        if (!alive) return;
        setItems(data.items || []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Eroare la încărcare declarații de traseu");
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (userId) load();

    function onCreated(ev) {
      try {
        const uid = ev?.detail?.userId;
        if (!uid || String(uid) !== String(userId)) return;
      } catch { /* ignore */ }
      if (alive) load();
    }

    window.addEventListener("routeDeclarationRevisionCreated", onCreated);
    return () => {
      alive = false;
      window.removeEventListener("routeDeclarationRevisionCreated", onCreated);
    };
  }, [userId]);

  async function handleDownload(revId, variant = "unsigned") {
    try {
      setDownloading(revId + "_" + variant);
      const { blob, filename } = await downloadPlatformDocumentRevision(revId, variant);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `declaratie_traseu_${revId}_${variant}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert(e?.message || "Eroare la descărcare");
    } finally {
      setDownloading(null);
    }
  }

  async function handleSendForSignature(revId) {
    if (!window.confirm("Trimiți declarația de traseu la FlexiDocs pentru semnare electronică?")) return;
    try {
      setSending(revId);
      await sendRouteDeclarationForSignature(revId);
      await fetchList();
    } catch (e) {
      const code = e?.status;
      const msg = (e?.message || "").toString();
      if (code === 409 || msg.toLowerCase().includes("conflict") || msg.toLowerCase().includes("already sent")) {
        alert("Declarația a fost deja trimisă la semnare.");
      } else if (code === 502 || msg.toLowerCase().includes("flexidocs")) {
        alert("Eroare la comunicarea cu FlexiDocs: " + msg);
      } else {
        alert(msg || "Eroare la trimitere la semnare");
      }
      await fetchList();
    } finally {
      setSending(null);
    }
  }

  if (loading) return <div className="text-sm text-slate-500 py-3">Se încarcă…</div>;
  if (err) return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>;

  if (!items || items.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3">
        Nu există declarații de traseu generate pentru acest utilizator.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">Generat la</th>
            <th className="py-2 pr-4">Semnat la</th>
            <th className="py-2 pr-4">Acțiuni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="py-2.5 pr-4 text-slate-700">{mapStatusLabel(r.status)}</td>
              <td className="py-2.5 pr-4 text-slate-500">
                {r.generated_at ? new Date(r.generated_at).toLocaleString() : "—"}
              </td>
              <td className="py-2.5 pr-4 text-slate-500">
                {r.signed_at ? new Date(r.signed_at).toLocaleString() : "—"}
              </td>
              <td className="py-2.5">
                <div className="flex gap-2 flex-wrap">
                  <button
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors ${!r.has_unsigned_pdf ? "opacity-60 cursor-not-allowed" : ""}`}
                    onClick={() => r.has_unsigned_pdf && handleDownload(r.id, "unsigned")}
                    disabled={!r.has_unsigned_pdf || downloading === r.id + "_unsigned"}
                  >
                    {downloading === r.id + "_unsigned" ? "Se descarcă..." : "Descarcă PDF"}
                  </button>

                  {r.status === "generated" && r.has_unsigned_pdf && (
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm transition-colors"
                      onClick={() => handleSendForSignature(r.id)}
                      disabled={sending === r.id}
                    >
                      {sending === r.id ? "Se trimite..." : "Trimite la semnare"}
                    </button>
                  )}

                  {r.has_signed_pdf && (
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-white hover:bg-slate-900 shadow-sm transition-colors"
                      onClick={() => handleDownload(r.id, "signed")}
                      disabled={downloading === r.id + "_signed"}
                    >
                      {downloading === r.id + "_signed" ? "Se descarcă..." : "Descarcă PDF semnat"}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
